import type { Theme } from "@mui/material/styles";
import type { ChangelogType } from "@xop/dashboard-shared";

/**
 * Change-type → human label + theme-palette dot colour.
 *
 * `dot` is a resolver `(theme) => colour` (was an OKLCH `var(--…)` Tailwind
 * class) so the markers re-skin with the MUI palette — same pattern as the
 * marketing pills. Consumers render it as the `bgcolor` of a small circle.
 *
 * `docs`/`refactor` lean on the per-system accent sub-palette
 * (`palette.system.*`); read it through `systemAccent` so the resolver
 * degrades to a core-palette colour under a bare MUI theme (e.g. a unit
 * test that renders the panel without the app's ThemeProvider) instead of
 * throwing on `palette.system` being undefined.
 */
function systemAccent(t: Theme, key: "docs" | "support", fallback: string): string {
  return t.palette.system?.[key] ?? fallback;
}

export const TYPE_META: Record<
  ChangelogType,
  { label: string; dot: (t: Theme) => string }
> = {
  feat: { label: "Feature", dot: (t) => t.palette.primary.main },
  fix: { label: "Fix", dot: (t) => t.palette.success.main },
  build: { label: "Build", dot: (t) => t.palette.warning.main },
  docs: { label: "Docs", dot: (t) => systemAccent(t, "docs", t.palette.secondary.main) },
  refactor: {
    label: "Refactor",
    dot: (t) => systemAccent(t, "support", t.palette.info.main),
  },
  perf: { label: "Perf", dot: (t) => t.palette.secondary.main },
  chore: { label: "Chore", dot: (t) => t.palette.text.disabled },
  security: { label: "Security", dot: (t) => t.palette.error.main },
};

/** Short, friendly repo labels for badges (drops the `xop-` prefix). */
export function repoLabel(repo: string): string {
  return repo.replace(/^xop-/, "");
}
